export interface URLInfo {
  roomName: string | null
  isOmniMode: boolean
  isLocalMode: boolean
  isSharedScreenMode: boolean
}

export function getRoomNameFromURL(): string | null {
  if (typeof window === 'undefined') {
    return null
  }
  const pathname = window.location.pathname
  const match = pathname.match(/^\/r\/([^\/]+)/)
  return match ? match[1] : null
}

/**
 * The `test:` prefix is the Playwright/BDD test-harness token for entering Omni
 * mode via a URL. It is an internal harness convention only - never a
 * product/creator-facing route - so it keeps its historical literal spelling
 * even though the capability itself is now called Omni mode.
 */
export function extractRoomNameFromOmniMode(roomName: string): string {
  if (roomName.startsWith('test:')) {
    return roomName.substring(5)
  }
  return roomName
}

export function isOmniModeFromRoomName(roomName: string | null): boolean {
  return roomName !== null && roomName.startsWith('test:')
}

export function isSharedScreenModeFromRoomName(roomName: string | null): boolean {
  return roomName !== null && roomName.startsWith('shared:')
}

export function isLocalModeFromRoomName(roomName: string | null): boolean {
  if (!roomName) return false
  if (roomName === 'local') return true
  if (roomName.startsWith('test:')) {
    return extractRoomNameFromOmniMode(roomName) === 'local'
  }
  if (roomName.startsWith('shared:')) {
    return roomName.substring(7) === 'local'
  }
  return false
}

export function getURLInfo(): URLInfo {
  const roomName = getRoomNameFromURL()
  return {
    roomName,
    isOmniMode: isOmniModeFromRoomName(roomName),
    isLocalMode: isLocalModeFromRoomName(roomName),
    isSharedScreenMode: isSharedScreenModeFromRoomName(roomName),
  }
}

export function parseRoomId(roomName: string): { mode: string | undefined, id: string } {
  const parts = roomName.split(':')
  if (parts.length > 1) {
    return { mode: parts[0], id: parts.slice(1).join(':') }
  }
  return { mode: undefined, id: roomName }
}
